// Worth a look (SCENARIOS E1, DIRECTION U27, PLAN-2026-10-27-worth-a-look §5):
// who on no team in my league has scored the most lately, at each position the
// lineup starts, and who is next up behind a starter who is out.
//
// A SHORTLIST WITH ITS REASONS. Each row is the Players list's own: the name
// opens his card, Add or Claim opens the same sheet, and the figure is his
// points a game over the league's last two weeks that have ended, each week's
// beside it at Standard. Nothing says to add anybody (tools, not instructions);
// the first of each position carries a flame because it is the list's first.
//
// ONE COLUMN ON A PHONE, the positions two to a row on a tablet and three on a
// desktop; Next man up after them, a column of its own beside them on a large
// screen. The practice league reads its simulator, which keeps no depth chart.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PlayerRowView, Snapshot, WorthALookView, WorthItem } from '@fsp/domain';
import { useAtLeast } from '../data/detail';
import { useLeagueBase, useReaderZone, useWorthALook } from '../data/hooks';
import { PositionPill, ProTeamCode, StatusBadge } from '../kit';
import { Icon } from '../kit/Icon';
import { Panel } from '../kit/Panel';
import { Button, Line, Stack, Text, Title } from '../kit/ui';
import { useTier } from '../shell/useTier';
import { FromLeague } from './shared/FromLeague';
import { PlayerCard, type PlayerCardData } from './shared/PlayerCard';
import { RosterMoveSheet, fmtWhen, type RosterMove } from './shared/RosterMoveSheet';
import { WorthInfo } from './shared/WorthHow';
import { pointsText, weeksText } from './shared/worthWords';
import '../styles/screens/worth.css';

function actionLabel(r: PlayerRowView): string {
  if (r.availability.kind === 'waivers' && r.availability.myClaimId) return 'Claimed';
  return r.action === 'claim' ? 'Claim' : 'Add';
}

export function WorthALookScreen() {
  const snapshot = useWorthALook();
  return (
    <div className="fsp-screen">
      <FromLeague snapshot={snapshot as Snapshot<WorthALookView>}>
        {(view) => <WorthBody view={view} />}
      </FromLeague>
    </div>
  );
}

function WorthBody({ view }: { view: WorthALookView }) {
  const tier = useTier();
  const base = useLeagueBase();
  const navigate = useNavigate();
  const zone = useReaderZone();
  // Simple keeps the one figure; Standard adds each week's and the player
  // ahead's; Everything the share of the site's leagues rostering him.
  const numbers = useAtLeast('standard');
  const everything = useAtLeast('pro');
  const [card, setCard] = useState<PlayerCardData | null>(null);
  const [move, setMove] = useState<RosterMove | null>(null);

  const act = (r: PlayerRowView) => {
    if (r.action !== 'add' && r.action !== 'claim') return;
    setCard(null);
    setMove({
      kind: r.action,
      playerId: r.player.id,
      claimId: r.availability.kind === 'waivers' ? r.availability.myClaimId : null,
    });
  };
  const open = (r: PlayerRowView) =>
    setCard({
      player: r.player,
      game: r.game,
      projection: r.projection,
      basis: r.basis,
      points: null,
      statLine: null,
      action:
        r.action === 'add' || r.action === 'claim'
          ? { label: actionLabel(r), onClick: () => act(r) }
          : null,
    });

  // TURNED OFF BY THE LEAGUE (RULES_SCHEMA §15c): the page says so, rather
  // than read as nobody being worth a look, and Players is a press away.
  if (view.off)
    return (
      <div className="fsp-worth" data-tier={tier}>
        <Stack gap={4}>
          <Title>Worth a look</Title>
          <Panel title="Off in this league">
            <div className="fsp-worth-note">
              <Text tone="secondary" block>
                This league turned off Worth a look and trending players in its helper tools. Every
                player on no team is still on Players.
              </Text>
              <div>
                <Button size="xs" variant="secondary" onClick={() => navigate(`${base}/players`)}>
                  Players
                </Button>
              </div>
            </div>
          </Panel>
        </Stack>
      </div>
    );

  const started = view.weeks.length > 0;
  const nextUp =
    view.nextUp.length === 0 ? null : (
      <Panel title="Next man up" className="fsp-worth-next">
        <div role="list" aria-label="Next man up">
          {view.nextUp.map((item) => (
            <WorthRow
              key={item.row.player.id}
              item={item}
              view={view}
              numbers={numbers}
              everything={everything}
              zone={zone}
              onOpen={open}
              onAct={act}
            />
          ))}
        </div>
      </Panel>
    );

  return (
    <div className="fsp-worth" data-tier={tier}>
      <Stack gap={4}>
        <Stack gap={1}>
          <Line gap={1}>
            <Title>Worth a look</Title>
            <WorthInfo />
          </Line>
          <Text tone="secondary" block>
            {started
              ? `On no team in this league · points a game in ${weeksText(view)}`
              : 'On no team in this league'}
          </Text>
        </Stack>
        {!started ? (
          <Panel title="Starts after the first week">
            <div className="fsp-worth-note">
              <Text tone="secondary" block>
                Who has scored lately shows once a week’s games are final: the players on no team
                with the most points a game, at each position your lineup starts.
              </Text>
              <div>
                <Button size="xs" variant="secondary" onClick={() => navigate(`${base}/players`)}>
                  Players
                </Button>
              </div>
            </div>
          </Panel>
        ) : null}
        <div className="fsp-worth-layout">
          {started ? (
            <div className="fsp-worth-grid">
              {view.positions.map((p) => (
                <Panel
                  key={p.id}
                  title={p.name}
                  meta={<PositionPill pos={p.id} size="sm" />}
                  className="fsp-worth-pos"
                >
                  {p.items.length === 0 ? (
                    <div className="fsp-worth-note">
                      <Text tone="secondary" size="sm" block>
                        Nobody available scored here in {weeksText(view)}.
                      </Text>
                    </div>
                  ) : (
                    <div role="list" aria-label={p.name}>
                      {p.items.map((item) => (
                        <WorthRow
                          key={item.row.player.id}
                          item={item}
                          view={view}
                          numbers={numbers}
                          everything={everything}
                          zone={zone}
                          onOpen={open}
                          onAct={act}
                        />
                      ))}
                    </div>
                  )}
                </Panel>
              ))}
            </div>
          ) : null}
          {nextUp}
        </div>
      </Stack>
      <PlayerCard data={card} onClose={() => setCard(null)} />
      <RosterMoveSheet move={move} showProjection onClose={() => setMove(null)} />
    </div>
  );
}

/** "On waivers until Wed 4:00 AM", "Free agent": where he stands. */
function standing(r: PlayerRowView, zone: string | null): string {
  if (r.availability.kind === 'waivers')
    return zone !== null
      ? `On waivers until ${fmtWhen(r.availability.clearsAt, zone)}`
      : 'On waivers';
  return 'Free agent';
}

function WorthRow({
  item,
  view,
  numbers,
  everything,
  zone,
  onOpen,
  onAct,
}: {
  item: WorthItem;
  view: WorthALookView;
  numbers: boolean;
  everything: boolean;
  zone: string | null;
  onOpen: (r: PlayerRowView) => void;
  onAct: (r: PlayerRowView) => void;
}) {
  const r = item.row;
  const because = item.because;
  const label = (period: number) =>
    view.weeks.find((w) => w.period === period)?.label.replace(/^Week /, 'Wk ') ?? '';
  return (
    <div
      role="listitem"
      className="fsp-row fsp-row-clickable fsp-worth-row"
      data-first={(because.kind === 'lately' && because.rank === 1) || undefined}
      // THE NAME IS THE BUTTON; THE ROW IS A BIGGER TARGET FOR IT (B-275).
      data-ux-convenience=""
      onClick={() => onOpen(r)}
    >
      <div className="fsp-worth-lead" aria-hidden={because.kind === 'lately' || undefined}>
        {because.kind === 'lately' ? (
          <span className="fsp-worth-rank fsp-num">
            {because.rank === 1 ? <Icon name="flame" size="sm" /> : because.rank}
          </span>
        ) : (
          <PositionPill pos={r.player.positions[0] ?? ''} size="sm" />
        )}
      </div>
      <div className="fsp-worth-who">
        <div className="fsp-name">
          <button
            type="button"
            className="fsp-linklike fsp-name-text fsp-name-btn"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(r);
            }}
          >
            {r.player.name}
          </button>
          <ProTeamCode code={r.player.proTeam} />
          <StatusBadge player={r.player} />
        </div>
        {because.kind === 'next-up' ? (
          <div className="fsp-sub fsp-ui-wrap-text">
            Behind {because.ahead.name} ({because.column}
            {because.ahead.injury !== null ? `, ${because.ahead.injury.name}` : ''})
            {numbers && because.aheadAvg !== null
              ? ` · ${pointsText(because.aheadAvg)} a game this season`
              : ''}
          </div>
        ) : null}
        <div className="fsp-sub fsp-worth-sub fsp-ui-wrap-text">
          {standing(r, zone)}
          {everything && r.ownershipPct !== null ? ` · ${r.ownershipPct}% rostered` : ''}
        </div>
        {numbers && item.weeks.length > 0 ? (
          <div className="fsp-worth-weeks" aria-label="Each week">
            {item.weeks.map((w) => (
              <span key={w.period} className="fsp-worth-week" data-kind={w.kind}>
                <span className="fsp-worth-week-label">{label(w.period)}</span>{' '}
                <span className="fsp-num">
                  {w.points !== null ? pointsText(w.points) : w.kind === 'bye' ? 'BYE' : '—'}
                </span>
              </span>
            ))}
          </div>
        ) : null}
        {r.action === 'none' && r.reason !== null ? (
          <div className="fsp-sub fsp-muted fsp-ui-wrap-text">{r.reason}</div>
        ) : null}
      </div>
      <div className="fsp-worth-figure">
        {item.lately !== null ? (
          <>
            <span className="fsp-num fsp-worth-figure-n">{pointsText(item.lately)}</span>
            <span className="fsp-worth-figure-unit">a game</span>
          </>
        ) : view.weeks.length > 0 ? (
          <span className="fsp-worth-figure-unit">no games</span>
        ) : null}
      </div>
      <div className="fsp-worth-act" data-ux-stop="" onClick={(e) => e.stopPropagation()}>
        {r.action === 'add' || r.action === 'claim' ? (
          <Button
            size="xs"
            full
            variant={
              r.availability.kind === 'waivers' && r.availability.myClaimId
                ? 'secondary'
                : r.action === 'claim'
                  ? 'caution-soft'
                  : 'soft'
            }
            onClick={() => onAct(r)}
            label={`${actionLabel(r)} ${r.player.name}`}
          >
            {actionLabel(r)}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
